/**
 * LeadDetailView — full info + activities + status change.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, Edit3, Plus,
  RefreshCw, Trash2,
} from "lucide-react";
import { API } from "../../lib/api";

const STATUS_COLOR = {
  "New Lead": "#1E5AA8", "Cold Prospect": "#6B7A90", "Prospective": "#2E86DE",
  "Hot Prospect": "#F39C12", "New Account Sign": "#27AE60",
  "Existing Account": "#0B2545", "Churn Account": "#8E44AD", "Lost Lead": "#E74C3C",
};

export default function LeadDetailView({ leadId, onBack, onEdit }) {
  const [lead, setLead] = useState(null);
  const [meta, setMeta] = useState(null);
  const [rmList, setRmList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actType, setActType] = useState("");
  const [actRemark, setActRemark] = useState("");
  const [submittingAct, setSubmittingAct] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusRemark, setStatusRemark] = useState("");
  const [reasonType, setReasonType] = useState("");
  const [tglSigning, setTglSigning] = useState("");
  const [tglBerakhir, setTglBerakhir] = useState("");
  const [assignedRm, setAssignedRm] = useState("");
  const [submittingStatus, setSubmittingStatus] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        axios.get(`${API}/leads/${leadId}`),
        axios.get(`${API}/leads/meta`),
        axios.get(`${API}/leads/meta/rms`).catch(() => ({ data: { rms: [] } })),
      ]);
      setLead(a.data); setMeta(b.data); setRmList(c.data.rms || []); setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal load");
    } finally { setLoading(false); }
  }, [leadId]);
  useEffect(() => { load(); }, [load]);

  const addActivity = async () => {
    if (!actType || !actRemark.trim()) return;
    setSubmittingAct(true);
    try {
      await axios.post(`${API}/leads/${leadId}/activities`, {
        activity_type: actType, remark: actRemark.trim(),
      });
      setActType(""); setActRemark(""); load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal tambah aktivitas");
    } finally { setSubmittingAct(false); }
  };

  const submitStatusChange = async () => {
    if (!newStatus) return;
    if (newStatus === "New Account Sign" && !assignedRm) {
      setError("Wajib pilih RM yang ditugaskan untuk lead ini sebelum promote ke New Account Sign");
      return;
    }
    setSubmittingStatus(true);
    try {
      const body = { status: newStatus, remark: statusRemark, reason_type: reasonType || null };
      if (newStatus === "New Account Sign") {
        body.tanggal_signing = tglSigning;
        body.tanggal_berakhir_kerjasama = tglBerakhir;
        body.assigned_rm = assignedRm;
      }
      await axios.post(`${API}/leads/${leadId}/status`, body);
      setStatusOpen(false); setNewStatus(""); setStatusRemark(""); setReasonType("");
      setTglSigning(""); setTglBerakhir(""); setAssignedRm("");
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Gagal ubah status");
    } finally { setSubmittingStatus(false); }
  };

  if (loading || !lead) return <div className="empty">Memuat detail...</div>;

  return (
    <div className="form-page" data-testid="lead-detail-view">
      <div className="form-page-head">
        <button className="btn btn-ghost" onClick={onBack} data-testid="lead-detail-back"><ArrowLeft size={14}/> Kembali ke daftar</button>
        <div style={{flex: 1}}>
          <h2 style={{margin: 0, color: "var(--navy)", fontSize: 22}}>{lead.nama_account}</h2>
          <p style={{margin: "4px 0 0 0", color: "var(--muted)", fontSize: 13}}>
            NPSN: <b>{lead.npsn || "—"}</b> · Sales: <b>{lead.assigned_to}</b> · Category: <b>{lead.category}</b>
          </p>
        </div>
        <button className="btn btn-secondary" onClick={() => onEdit && onEdit(lead.id)} data-testid="lead-edit"><Edit3 size={14}/> Edit Lead</button>
      </div>

      {error && <div className="alert err" style={{margin: "16px 0"}}><AlertTriangle size={14}/> {error}</div>}

      <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 22}}>
        <div className="card" style={{padding: 22}}>
          <h4 style={{margin: "0 0 10px 0", color: "var(--navy)"}}>Status & Pipeline</h4>
          <div style={{display: "flex", alignItems: "center", gap: 12}}>
            <span style={{
              background: (STATUS_COLOR[lead.status_lead] || "#6B7A90") + "22",
              color: STATUS_COLOR[lead.status_lead] || "#6B7A90",
              border: `1px solid ${STATUS_COLOR[lead.status_lead] || "#6B7A90"}55`,
              padding: "5px 14px", borderRadius: 14, fontWeight: 700, fontSize: 13,
            }}>{lead.status_lead}</span>
            <button className="btn btn-ghost" onClick={() => setStatusOpen(!statusOpen)} data-testid="lead-change-status">
              Ubah Status <ChevronRight size={12} />
            </button>
          </div>
          {statusOpen && meta && (
            <div style={{marginTop: 14, padding: 12, background: "var(--bg-2)", borderRadius: 8}}>
              <select className="form-input" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="status-select">
                <option value="">— Pilih status baru —</option>
                {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {newStatus === "New Account Sign" && (
                <>
                  <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8}}>
                    <input className="form-input" type="date" placeholder="Tanggal Signing" value={tglSigning} onChange={(e) => setTglSigning(e.target.value)} data-testid="status-tgl-signing" />
                    <input className="form-input" type="date" placeholder="Tanggal Berakhir" value={tglBerakhir} onChange={(e) => setTglBerakhir(e.target.value)} data-testid="status-tgl-berakhir" />
                  </div>
                  <div style={{marginTop: 10, padding: 12, background: "rgba(243,156,18,.08)",
                                border: "1px solid #F5C772", borderRadius: 8}}>
                    <label style={{fontSize: 12, fontWeight: 700, color: "#8B5A0F", display: "block", marginBottom: 6}}>
                      🎯 RM yang ditugaskan * (wajib) — lead ini akan masuk KPI RM tersebut
                    </label>
                    <select className="form-input" value={assignedRm}
                      onChange={(e) => setAssignedRm(e.target.value)} data-testid="status-assigned-rm">
                      <option value="">— Pilih Relationship Manager —</option>
                      {rmList.map((rm) => (
                        <option key={rm.username} value={rm.username}>
                          {rm.name} (@{rm.username}){rm.team ? ` · Team ${rm.team}` : ""}
                        </option>
                      ))}
                    </select>
                    {rmList.length === 0 && (
                      <div style={{fontSize: 11, color: "#8B5A0F", marginTop: 6}}>
                        Tidak ada RM aktif. Admin perlu membuat akun bertipe RM terlebih dahulu.
                      </div>
                    )}
                  </div>
                </>
              )}
              {newStatus === "Lost Lead" && (
                <select className="form-input" value={reasonType} onChange={(e) => setReasonType(e.target.value)} style={{marginTop: 8}}>
                  <option value="">— Pilih reason type —</option>
                  {meta.lost_reasons.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.label}</option>)}
                </select>
              )}
              <textarea className="form-input" placeholder="Remark..." value={statusRemark}
                onChange={(e) => setStatusRemark(e.target.value)} style={{marginTop: 8, minHeight: 60, resize: "vertical"}} />
              <div style={{display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end"}}>
                <button className="btn btn-secondary" onClick={() => setStatusOpen(false)}>Batal</button>
                <button className="btn btn-primary" onClick={submitStatusChange} disabled={!newStatus || submittingStatus} data-testid="status-submit">
                  {submittingStatus ? <RefreshCw size={14} className="spin"/> : <CheckCircle2 size={14}/>} Simpan
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="card" style={{padding: 22}}>
          <h4 style={{margin: "0 0 10px 0", color: "var(--navy)"}}>Estimated Deal Value</h4>
          <div style={{fontSize: 26, fontWeight: 800, color: "var(--good)"}}>
            {lead.estimated_value ? `Rp ${lead.estimated_value.toLocaleString("id-ID")}` : "—"}
          </div>
          <p style={{fontSize: 11, color: "var(--muted)", margin: "4px 0 0 0"}}>
            {lead.jumlah_student || 0} student × {lead.course_package || "(no package)"}
          </p>
        </div>
      </div>

      <section className="form-section">
        <h3 className="form-section-title">Tambah Aktivitas</h3>
        <div className="form-grid">
          <div className="form-field"><label>Activity Type</label>
            <select className="form-input" value={actType} onChange={(e) => setActType(e.target.value)} data-testid="act-type">
              <option value="">— Pilih —</option>
              {meta?.activity_types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></div>
          <div className="form-field"><label>Remark</label>
            <input className="form-input" value={actRemark} onChange={(e) => setActRemark(e.target.value)} data-testid="act-remark" /></div>
        </div>
        <div style={{textAlign: "right", marginTop: 10}}>
          <button className="btn btn-primary" onClick={addActivity} disabled={!actType || !actRemark.trim() || submittingAct} data-testid="act-submit">
            {submittingAct ? <RefreshCw size={14} className="spin"/> : <Plus size={14}/>} Tambah
          </button>
        </div>
      </section>

      <section className="form-section">
        <h3 className="form-section-title">Riwayat Aktivitas ({(lead.activities || []).length})</h3>
        {(lead.activities || []).length === 0 ? (
          <div className="empty">Belum ada aktivitas.</div>
        ) : (
          <div style={{display: "flex", flexDirection: "column", gap: 10}}>
            {[...lead.activities].reverse().map((a, i) => (
              <div key={i} style={{padding: 12, background: "var(--bg)", borderRadius: 8, border: "1px solid var(--border-2)"}}>
                <div style={{display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 4}}>
                  <span style={{fontWeight: 600, color: "var(--navy)", fontSize: 13}}>{a.activity_type}</span>
                  <span style={{fontSize: 11, color: "var(--muted)"}}>
                    {new Date(a.tanggal).toLocaleString("id-ID")} · oleh {a.by}
                  </span>
                </div>
                <p style={{margin: 0, fontSize: 13, color: "var(--text)"}}>{a.remark}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="form-section">
        <h3 className="form-section-title">Status History</h3>
        <ol style={{margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--text)"}}>
          {(lead.status_history || []).map((h, i) => (
            <li key={i} style={{marginBottom: 6}}>
              <b style={{color: "var(--navy)"}}>{h.status}</b>
              <span style={{color: "var(--muted)", marginLeft: 8, fontSize: 11}}>
                {new Date(h.at).toLocaleString("id-ID")} · {h.by}
                {h.reason_type && ` · ${h.reason_type}`}
                {h.remark && ` — ${h.remark}`}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
